import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faEmptySet,
  faHand,
  faTriangleExclamation,
  faMessageCode,
  faCheckCircle,
  faTrophy,
} from '@fortawesome/pro-light-svg-icons';
import { combine } from '../../../../helpers/styles';
import ProgressCircle from '../../../progressCircle';
import { ROLES } from '../../../../config/permissions/roles';
import {
  getAverageScore,
  getProgressPrecentage,
} from '../../../../helpers/statistic';
import {
  GROUP_ROLE,
  AWARD_ROLE,
  PREDECESSOR_SESSION_GROUP_ROLE,
} from '../../../../models/evaluationStatistic';
import { AWARD_TYPES } from '../../../../models/award';
import { useJuror } from '../../../../hooks/useAuth';
import { isAwardType } from '../../../../helpers/awardTypes';
import { SESSION_TYPES } from '../../../../models/session';
import iFGoldAwardLogo from '../../../../assets/images/iF-Gold.svg';
import styles from './styles.module.scss';

const MainStatistic = ({ isLoading, statistic, filter, bigStats }) => {
  const { t } = useTranslation();
  const { session } = useJuror();

  const mappedStatistics = useMemo(() => {
    if (!filter || !statistic) return [];

    if (filter === 'group' && isAwardType(AWARD_TYPES.IF_DA, session)) {
      const chairpersonStat = statistic.jurors.find(
        (statisticItem) => statisticItem.role === ROLES.chairPerson
      );
      const groupStat = statistic.jurors.find(
        (statisticItem) => statisticItem.role === GROUP_ROLE
      );
      const awardStat = statistic.jurors.find(
        (statisticItem) => statisticItem.role === AWARD_ROLE
      );
      const preselectionStat = statistic.jurors.find(
        (statisticItem) => statisticItem.role === PREDECESSOR_SESSION_GROUP_ROLE
      );

      const decidedSubmissionsAboveThreshold =
        session?.sessionType === SESSION_TYPES.PRESELECTION
          ? statistic.numFullyDecidedSubmissionsWithPointsAboveSessionMedianOneHalve
          : statistic.numFullyDecidedSubmissionsWithPointsAboveSessionMedianOneThird;

      const numEvaluatingJurors = statistic.jurors.filter((juror) =>
        [ROLES.juror, ROLES.groupLeader].includes(juror.role)
      ).length;

      // Calculate the number of fully evaluated submissions
      // PRESELECTION requires 3 evaluations per submission, FINAL_JURY requires 1
      const numEvaluationsPerSubmission =
        session?.sessionType === SESSION_TYPES.PRESELECTION ? 3 : 1;
      const evaluatedSubmissionsCount =
        groupStat.decidedEvaluationCount / numEvaluationsPerSubmission;

      const finalistsPercentage =
        evaluatedSubmissionsCount && decidedSubmissionsAboveThreshold
          ? (
              (decidedSubmissionsAboveThreshold / evaluatedSubmissionsCount) *
              100
            ).toFixed()
          : 0;

      const statistics = {
        toBeDecidedByChairperson: {
          icon: faHand,
          label: t('jurorDashboard.statistic.group.decideByChairperson'),
          type: 'toBeDecidedByChairperson',
          value: chairpersonStat.totalEvaluationCount,
        },
        progressPercentage: {
          type: 'progressPercentage',
          value: getProgressPrecentage(
            groupStat.decidedEvaluationCount,
            groupStat.totalEvaluationCount
          ),
          // This calulation will only work for a single evaluating juror per group.
          // We only use it for Final Jury, which only has a single evaluating juror, so it should be fine.
          subValue:
            session?.sessionType === SESSION_TYPES.FINAL_JURY &&
            t('jurorDashboard.statistic.group.progressPercentageSubValue', {
              numDone: groupStat.decidedEvaluationCount,
              numTotal: groupStat.totalEvaluationCount,
              awardProgress: getProgressPrecentage(
                awardStat.decidedEvaluationCount,
                awardStat.totalEvaluationCount
              ),
            }),
        },
        averageScore: {
          icon: faEmptySet,
          label: t('jurorDashboard.statistic.group.averageScore'),
          type: 'averageScore',
          value: getAverageScore(
            groupStat.decidedEvaluationCount,
            groupStat.totalPointsFromAllDecidedEvaluations
          ),
          subValue:
            session?.sessionType === SESSION_TYPES.FINAL_JURY &&
            preselectionStat &&
            t('jurorDashboard.statistic.group.averageScoreSubValue', {
              awardScore: getAverageScore(
                awardStat.decidedEvaluationCount,
                awardStat.totalPointsFromAllDecidedEvaluations
              ),
              preselectionScore: getAverageScore(
                preselectionStat.decidedEvaluationCount,
                preselectionStat.totalPointsFromAllDecidedEvaluations
              ),
            }),
        },
        scoreAlerts: {
          icon: faTriangleExclamation,
          label: t('jurorDashboard.statistic.group.scoreAlerts'),
          type: 'scoreAlerts',
          value: statistic.numScoreAlerts,
        },
        numComments: {
          icon: faMessageCode,
          label: t('jurorDashboard.statistic.group.comments'),
          type: 'numComments',
          value: statistic.numEntriesWithNotDiscussedComment,
        },
        finalists: {
          icon: faTrophy,
          label: t('jurorDashboard.statistic.group.finalists'),
          type: 'finalists',
          value: `${finalistsPercentage}%`,
        },
        nominated: {
          img: iFGoldAwardLogo,
          label: t('jurorDashboard.statistic.group.nominated'),
          type: 'nominated',
          value: statistic.numSubmissionsWithSuggestedForNomination,
          subValue: t('jurorDashboard.statistic.group.goldDecided', {
            count: statistic.numSubmissionsWithNomination,
          }),
        },
      };

      const preselectionStats = [
        statistics.toBeDecidedByChairperson,
        statistics.progressPercentage,
        statistics.averageScore,
        statistics.scoreAlerts,
        statistics.numComments,
        statistics.finalists,
      ];

      const finalJuryStats = [
        statistics.progressPercentage,
        statistics.averageScore,
        statistics.nominated,
      ];

      return session?.sessionType === SESSION_TYPES.PRESELECTION
        ? preselectionStats
        : finalJuryStats;
    } else {
      const stat = statistic.jurors.find(
        (jurorStatistic) => jurorStatistic.jurorId === filter
      );

      if (!stat) return [];

      return [
        {
          icon: faCheckCircle,
          label: t('jurorDashboard.statistic.main.entries.label'),
          type: 'decided',
          value: t('jurorDashboard.statistic.main.entries.value', {
            numDecided: stat?.decidedEvaluationCount,
            total: stat?.totalEvaluationCount,
          }),
        },
        {
          label: t('jurorDashboard.statistic.main.progress.label'),
          type: 'progressPercentage',
          value: getProgressPrecentage(
            stat.decidedEvaluationCount,
            stat.totalEvaluationCount
          ),
        },
        {
          icon: faEmptySet,
          label: t('jurorDashboard.statistic.main.score.label'),
          type: 'averageScore',
          value: getAverageScore(
            stat.decidedEvaluationCount,
            stat.totalPointsFromAllDecidedEvaluations
          ),
        },
      ];
    }
  }, [filter, statistic, session]);

  return (
    <div
      className={combine(
        styles.statisticContainer,
        session?.sessionType === SESSION_TYPES.FINAL_JURY && styles.finalJury,
        mappedStatistics.length > 3 && styles.doubleRows
      )}
    >
      {mappedStatistics.map((stat) => (
        <div
          key={stat.type}
          className={combine(styles.column, 'placeholder-glow')}
        >
          {isLoading ? (
            <span className={combine(styles.placeholder, 'placeholder')}></span>
          ) : (
            <>
              {stat.type === 'progressPercentage' ? (
                <>
                  <div className={styles.valueContainer}>
                    <ProgressCircle progress={stat.value} />
                    <div className={styles.bottomContainer}>
                      <span className={styles.label}>
                        {t('jurorDashboard.statistic.main.progress.label')}
                      </span>
                    </div>
                  </div>
                  {stat.subValue && (
                    <span
                      className={combine(
                        styles.subValue,
                        session?.sessionType === SESSION_TYPES.FINAL_JURY &&
                          styles.doubleRow
                      )}
                      dangerouslySetInnerHTML={{ __html: stat.subValue }}
                    ></span>
                  )}
                </>
              ) : (
                <>
                  <div className={styles.valueContainer}>
                    {stat.icon ? (
                      <FontAwesomeIcon
                        className={styles.icon}
                        icon={stat.icon}
                        size="2x"
                      />
                    ) : (
                      <img src={iFGoldAwardLogo} className={styles.icon} />
                    )}
                    <span className={styles.value}>{stat.value}</span>
                    <div className={styles.bottomContainer}>
                      <span className={styles.label}>{stat.label}</span>
                    </div>
                  </div>
                  {stat.subValue && (
                    <span
                      className={styles.subValue}
                      dangerouslySetInnerHTML={{ __html: stat.subValue }}
                    ></span>
                  )}
                </>
              )}
            </>
          )}
        </div>
      ))}
    </div>
  );
};

MainStatistic.propTypes = {
  isLoading: PropTypes.bool,
  statistic: PropTypes.shape({
    numScoreAlerts: PropTypes.number,
    numSubmissionsWithSuggestedForNomination: PropTypes.number,
    numEvaluationsWithCount: PropTypes.number,
    jurors: PropTypes.array,
  }),
  filter: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

MainStatistic.defaultProps = {
  isLoading: false,
  statistic: null,
  filter: '',
};

export default MainStatistic;
